import type { DbClient } from './types';
import { parseDbRows, rhColumnRowSchema } from '../rhDataSchemas';

/**
 * Carrega colunas Kanban e limites de contratação da empresa.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 */
export async function loadColumns(
  sb: DbClient,
  companyId: string,
): Promise<{ columns: { id: string; title: string; color?: string }[]; hiringLimits: Record<string, number> }> {
  const { data, error } = await sb
    .from('rh_columns')
    .select('*')
    .eq('company_id', companyId)
    .order('sort_order');
  if (error) throw error;

  const rows = parseDbRows(rhColumnRowSchema, data ?? [], 'rh_columns');
  const columns = rows.map((r) => ({ id: r.column_key, title: r.title, color: r.color }));
  const hiringLimits = Object.fromEntries(rows.map((r) => [r.column_key, r.hiring_limit]));
  return { columns, hiringLimits };
}

/**
 * Persiste colunas e limites de contratação (upsert por company_id + column_key).
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 * @param columns - Definição das colunas do board
 * @param hiringLimits - Limite por column_key
 */
export async function saveColumns(
  sb: DbClient,
  companyId: string,
  columns: { id: string; title: string; color?: string }[],
  hiringLimits: Record<string, number>,
): Promise<void> {
  const rows = columns.map((col, idx) => ({
    company_id: companyId,
    column_key: col.id,
    title: col.title,
    color: col.color ?? '#6B7280',
    hiring_limit: hiringLimits[col.id] ?? 0,
    sort_order: idx,
  }));
  const { error } = await sb.from('rh_columns').upsert(rows, { onConflict: 'company_id,column_key' });
  if (error) throw error;
}

/**
 * Remove uma coluna Kanban do banco e reordena as demais.
 * @param sb - Cliente API
 * @param companyId - UUID da empresa
 * @param columnKey - Chave da coluna (column_key)
 * @param remainingColumns - Colunas que permanecem após a exclusão
 * @param hiringLimits - Limites atualizados (sem a coluna removida)
 */
export async function deleteColumn(
  sb: DbClient,
  companyId: string,
  columnKey: string,
  remainingColumns: { id: string; title: string; color?: string }[],
  hiringLimits: Record<string, number>,
): Promise<void> {
  const { error: deleteError } = await sb
    .from('rh_columns')
    .delete()
    .eq('company_id', companyId)
    .eq('column_key', columnKey);
  if (deleteError) throw deleteError;
  await saveColumns(sb, companyId, remainingColumns, hiringLimits);
}
